import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import '../css/StockistVisitReport.css';
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import { FaSearch } from "react-icons/fa";

const StockistVisitReport = () => {
    const [states, setStates] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedState, setSelectedState] = useState("");
    const [selectedEmployee, setSelectedEmployee] = useState("All");
    const [tableData, setTableData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({
        state: false,
        employee: false
    });
    
    // Search functionality states
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const inputRef = useRef(null);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/states`, {
                    headers: getAuthHeaders()
                });
                setStates(response.data);
            } catch (err) {
                console.error("Error fetching states:", err);
            }
        };
        fetchStates();
    }, []);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    const handleExpandSearch = () => {
        setIsSearchExpanded(!isSearchExpanded);
        if (!isSearchExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const fetchEmployees = useCallback(async (stateId) => {
        if (!stateId) return;
        try {
            let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
            const res = await axios.get(url, {
                headers: getAuthHeaders()
            });
            setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
            setSelectedEmployee("All");
        } catch (err) {
            console.error("Error fetching employees:", err);
            setEmployees([]);
        }
    }, []);

    const handleStateChange = async (stateId) => {
        setSelectedState(stateId);
        setSelectedEmployee("All");
        setErrors(prev => ({ ...prev, state: false }));
        fetchEmployees(stateId);
    };

    const handleGoClick = async () => {
        const newErrors = {
            state: !selectedState,
            employee: false
        };

        setErrors(newErrors);

        if (newErrors.state) {
            return;
        }

        setShowTable(true);
        setIsLoading(true);
        try {
            let url = `${BASE_URL}/api/stockist/listwithsob?state_id=${selectedState}`;

            if (selectedEmployee && selectedEmployee !== "All") {
                url += `&employee_id=${selectedEmployee}`;
            }

            console.log("Making API request to:", url); // Debug log
            
            const response = await axios.get(url, {headers: getAuthHeaders()});
            
            console.log("API Response:", response); // Debug log
            
            // Ensure the response data structure is correct
            const data = Array.isArray(response.data?.data || response.data) 
                ? (response.data?.data || response.data) 
                : [];
            
            console.log("Processed data:", data); // Debug log
            
            setTableData(data);
            setFilteredData(data);
            setTotalRecords(data.length);
        } catch (error) {
            console.error("Error fetching report data:", error);
            setTableData([]);
            setFilteredData([]);
            setTotalRecords(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const filtered = searchQuery
            ? tableData.filter(item => {
                if (!item) return false;
                return Object.values(item).some(val => {
                    if (!val) return false;
                    return val.toString().toLowerCase().includes(searchQuery.toLowerCase());
                });
            }) : tableData;

        setFilteredData(filtered);
        setTotalRecords(filtered.length);
        setCurrentPage(1);
    }, [searchQuery, tableData]);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const day = date.getDate().toString().padStart(2, '0');
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
        } catch (e) {
            console.error("Error formatting date:", dateString, e);
            return "N/A";
        }
    };

    // Calculate paginated data
    const paginatedData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="StockistVisitReport-main-container">
            {isLoading && <Loader />}

            <div className={`main-container ${isLoading ? "blur-sm pointer-events-none" : ""}`}>
                <div className="StockistVisitReport-filter-container">
                    <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
                        <span className="text-blue-700"> Visit / </span>
                        <span className="text-blue-500">Support</span>
                    </h1>
                    <div className="StockistVisitReport-row1">
                        <label className="StockistVisitReport-label">State</label>
                        <select
                            className={`StockistVisitReport-dropdown ${errors.state ? 'error-border' : ''}`}
                            onChange={(e) => {
                                handleStateChange(e.target.value);
                                setErrors({ ...errors, state: false });
                            }}
                            value={selectedState}
                            required
                        >
                            <option value="">Select State</option>
                            {states.map((state) => (
                                <option key={state.id} value={state.id}>{state.name}</option>
                            ))}
                        </select>

                        <label className="StockistVisitReport-label">Employee</label>
                        <select
                            className={`StockistVisitReport-dropdown ${errors.employee ? 'error-border' : ''}`}
                            onChange={(e) => {
                                setSelectedEmployee(e.target.value);
                                setErrors({ ...errors, employee: false });
                            }}
                            value={selectedEmployee}
                        >
                            <option value="All">All</option>
                            {employees.map((emp) => (
                                emp.id !== "All" && (
                                    <option key={emp.id} value={emp.id}>
                                        {`${emp.first_name} ${emp.last_name} (${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name})`}
                                    </option>
                                )
                            ))}
                        </select>
                    </div>

                    <div className="StockistVisitReport-go-button-container">
                        <button className="StockistVisitReport-gobutton" onClick={handleGoClick}>GO</button>
                    </div>
                </div>

                {showTable && (
                    <>
                        <div className="StockistVisitReport-table-section">
                            <div className="StockistVisitReport-title3-container">
                                <h2 className="StockistVisitReport-table-title">
                                    {states.find(s => s.id == selectedState)?.name || selectedState} - All Assigned Stockist
                                </h2>
                                <div className="relative mb-4 flex justify-end">
                                    <form
                                        onSubmit={handleSearchSubmit}
                                        className="flex items-center gap-2"
                                    >
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            placeholder="Search stockists..."
                                            className={`transition-all duration-300 ease-in-out border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                                isSearchExpanded ? "w-64 opacity-100" : "w-0 opacity-0 px-0 py-0"
                                            }`}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onBlur={() => !searchQuery && setIsSearchExpanded(false)}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleExpandSearch}
                                            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                        >
                                            <FaSearch className="w-5 h-5" />
                                        </button>
                                    </form>
                                </div>
                            </div>
                            <div className="StockistVisitReport-table-wrapper">
                                <table className="StockistVisitReport-table">
                                    <thead className="text-white">
                                        <tr>
                                            <th>S.No</th>
                                            <th>State</th>
                                            <th>Emp ID</th>
                                            <th>Employee</th>
                                            <th>Division</th>
                                            <th>Manager</th>
                                            <th>Stockist Code</th>
                                            <th>Stockist</th>
                                            <th>HQ</th>
                                            <th>City</th>
                                            <th>Phone</th>
                                            <th>Added Date</th>
                                            <th>Latest Visit</th>
                                            <th>SOB</th>
                                            <th>Geo Mapped</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedData.length > 0 ? (
                                            paginatedData.map((item, index) => (
                                                <tr key={`${item.stockist_code}-${index}`}>
                                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                    <td>{item.state_name || 'N/A'}</td>
                                                    <td>{item.employee_id || 'N/A'}</td>
                                                    <td>{item.employee_name || 'N/A'}</td>
                                                    <td>{item.employee_division || 'N/A'}</td>
                                                    <td>{item.employee_manager || 'N/A'}</td>
                                                    <td>{item.stockist_code || 'N/A'}</td>
                                                    <td>{item.stockist_name || 'N/A'}</td>
                                                    <td>{item.headquarter_name || 'N/A'}</td>
                                                    <td>{item.city_name || 'N/A'}</td>
                                                    <td>{item.mobile || 'N/A'}</td>
                                                    <td>{formatDate(item.added_date)}</td>
                                                    <td>{formatDate(item.visit_date)}</td>
                                                    <td>{item.sob_achieved || 'N/A'}</td>
                                                    <td>{item.geo_mapped || "NA"}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="15" className="text-center py-4">
                                                    No data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* Pagination */}
                            {filteredData.length > 0 && (
                                <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
                                    <div className="flex items-center mb-2 sm:mb-0">
                                        <label
                                            htmlFor="itemsPerPage"
                                            className="mr-2 font-medium text-sm text-gray-700"
                                        >
                                            View
                                        </label>
                                        <select
                                            id="itemsPerPage"
                                            className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
                                            value={itemsPerPage}
                                            onChange={handleItemsPerPageChange}
                                        >
                                            <option value="10">10</option>
                                            <option value="20">20</option>
                                            <option value="30">30</option>
                                            <option value="50">50</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="text-sm text-gray-600 font-medium">
                                            {(currentPage - 1) * itemsPerPage + 1}-
                                            {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                                            {totalRecords}
                                        </span>
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            ←
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage * itemsPerPage >= totalRecords}
                                            className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {filteredData.length > 0 && (
                            <div className="StockistVisitReport-button-container">
                                <button className="StockistVisitReport-printbutton">Print</button>
                                <button className="StockistVisitReport-exportbutton">Export to Excel</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default StockistVisitReport;